import React, { useMemo, useEffect, useState, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams, useLocation, Link as RouterLink } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Autocomplete,
    TextField,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
    Chip,
    Avatar,
    Breadcrumbs,
    Link,
    Grid,
    Stack,
    Tooltip,
} from '@mui/material';
import {
    Business as BusinessIcon,
    Podcasts as PodcastsIcon,
    PlayArrow as PlayArrowIcon,
    ChevronRight as ChevronRightIcon,
    Instagram as InstagramIcon,
    SwipeLeft as SwipeLeftIcon,
} from '@mui/icons-material';
import TikTokIcon from '../components/icons/TikTokIcon';
import { ShortFormCreatorsTable, type ShortFormCreatorsTableRow, type ShortFormTableSortKey } from '../components/ShortFormCreatorsTable';
import { TrendChartCard } from './Explorer/TrendChartCard';
// recharts imports removed - using TrendChartCard component instead
import { ROUTES_NAMES } from '../components/Layout';
import PodscopePublicHeader from '../components/PodscopePublicHeader';
import apiService from '../services/api';
import { getS3Url, ensureDirectS3Url } from '../utils/imageUtils';
import { useBrandExplorerStore } from '../stores/brandExplorerStore';
import { useTrackerStore } from '../stores/trackerStore';
import { useAuth } from '../contexts/AuthContext';

// Helper function to calculate median
const calculateMedian = (values: number[]): number => {
    if (values.length === 0) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

interface BrandExplorerYearProps {
    fixedBrandId?: string;
}

const BrandExplorerYear: React.FC<BrandExplorerYearProps> = ({ fixedBrandId }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { selectedBrand, startDate: explorerStartDate, endDate: explorerEndDate, setSelectedBrand, setStartDate: setExplorerStartDate, setEndDate: setExplorerEndDate, reset } = useBrandExplorerStore();
    const queryClient = useQueryClient();
    const prefetchHoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const scheduleHoverPrefetch = useCallback((fn: () => void) => {
        if (prefetchHoverTimerRef.current) clearTimeout(prefetchHoverTimerRef.current);
        prefetchHoverTimerRef.current = setTimeout(() => {
            prefetchHoverTimerRef.current = null;
            fn();
        }, 280);
    }, []);

    useEffect(() => {
        return () => {
            if (prefetchHoverTimerRef.current) clearTimeout(prefetchHoverTimerRef.current);
        };
    }, []);

    const isTracker = location.pathname.startsWith('/tracker');

    // Use English for tonybet route (fixedBrandId), Spanish otherwise
    const isEnglish = !!fixedBrandId;

    // Get platform filter from URL (yt or ig)
    const platform = searchParams.get('platform') as 'yt' | 'ig' | null;

    // Tracker date filters and company-assigned brand (from auth context)
    const { getDateFilterMinStartDate, getDateFilterMaxEndDate, getCompanyTrackerBrandId } = useAuth();
    const companyTrackerBrandId = getCompanyTrackerBrandId();
    const TRACKER_MIN_DATE = getDateFilterMinStartDate();
    const TRACKER_MAX_DATE = getDateFilterMaxEndDate();
    const {
        startDate: trackerStartDate,
        endDate: trackerEndDate,
        setStartDate: setTrackerStartDate,
        setEndDate: setTrackerEndDate,
        resetDates: resetTrackerDates,
    } = useTrackerStore();

    // Clamp persisted tracker dates to company bounds when in tracker
    useEffect(() => {
        if (!isTracker) return;
        if (trackerStartDate < TRACKER_MIN_DATE || trackerEndDate > TRACKER_MAX_DATE) {
            resetTrackerDates(
                trackerStartDate < TRACKER_MIN_DATE ? TRACKER_MIN_DATE : trackerStartDate,
                trackerEndDate > TRACKER_MAX_DATE ? TRACKER_MAX_DATE : trackerEndDate
            );
        }
    }, [isTracker, TRACKER_MIN_DATE, TRACKER_MAX_DATE, trackerStartDate, trackerEndDate, resetTrackerDates]);

    // Tracker: brand and date live in Zustand only; no query params. Nothing to reset on entry.

    const TRACKER_BRAND_NAMES = [
        'TonyBet', 'Entel', 'Elite', 'Raid', 'McDonalds',
        'Blue Express', 'Escudo', 'Mercado Libre',
    ];

    // Fetch fixed tracker brands by name
    const { data: trackerFixedBrands, isLoading: trackerFixedLoading } = useQuery({
        queryKey: ['brands', 'tracker-fixed'],
        queryFn: async () => {
            const response = await apiService.getBrands(1, 20, undefined, undefined, undefined, undefined, TRACKER_BRAND_NAMES.join(','));
            return response.brands;
        },
        enabled: isTracker,
        staleTime: 30 * 60 * 1000,
    });

    // Search for additional brands in tracker
    const [trackerBrandSearch, setTrackerBrandSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(trackerBrandSearch), 300);
        return () => clearTimeout(timer);
    }, [trackerBrandSearch]);

    const { data: trackerSearchBrands, isLoading: trackerSearchLoading } = useQuery({
        queryKey: ['brands', 'tracker-search', debouncedSearch],
        queryFn: async () => {
            const response = await apiService.getBrands(1, 20, debouncedSearch, undefined, true, true);
            return response.brands;
        },
        enabled: isTracker && debouncedSearch.length >= 2,
        staleTime: 5 * 60 * 1000,
    });

    // Merge fixed + search results, deduplicated
    const trackerBrandsData = useMemo(() => {
        const fixed = trackerFixedBrands || [];
        const searched = trackerSearchBrands || [];
        const seen = new Set<string>();
        const merged: typeof fixed = [];
        for (const b of [...fixed, ...searched]) {
            if (!seen.has(b._id)) {
                seen.add(b._id);
                merged.push(b);
            }
        }
        return merged;
    }, [trackerFixedBrands, trackerSearchBrands]);

    const trackerBrandsLoading = trackerFixedLoading || (trackerSearchLoading && debouncedSearch.length >= 2);

    // Fetch brands for brand-explorer (not tracker)
    const { data: brandsData, isLoading: brandsLoading } = useQuery({
        queryKey: ['brands', 'all'],
        queryFn: async () => {
            const response = await apiService.getBrands(1, 500, undefined, undefined, true);
            return response.brands;
        },
        enabled: !fixedBrandId && !isTracker,
        staleTime: 5 * 60 * 1000,
    });

    const brands: Array<{ _id: string; name: string; category?: string; logoUrl?: string; s3Key?: string }> = isTracker ? (trackerBrandsData || []) : (brandsData || []);
    const activeBrandsLoading = isTracker ? trackerBrandsLoading : brandsLoading;

    // Fetch fixed brand if provided (using public API call for TonyBet)
    const { data: fixedBrandData, isLoading: fixedBrandLoading } = useQuery({
        queryKey: ['brand', fixedBrandId],
        queryFn: async () => {
            // For fixed brand (TonyBet), we need to fetch all brands and filter
            // Since GET /api/brands is now private, we'll use a workaround:
            // Call the brand-data endpoint (public for TonyBet) to get brand info
            try {
                const brandData = await apiService.getBrandData(fixedBrandId!);
                if (brandData && brandData.brand) {
                    return {
                        _id: brandData.brand._id,
                        name: brandData.brand.name,
                        category: brandData.brand.category,
                        logoUrl: brandData.brand.logoUrl,
                        s3Key: brandData.brand.s3Key,
                    };
                }
                return null;
            } catch (error) {
                console.error('Error fetching fixed brand:', error);
                return null;
            }
        },
        enabled: !!fixedBrandId,
        staleTime: 10 * 60 * 1000, // 10 minutes
        gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache for smooth navigation
    });

    // Fetch company-assigned Tracker brand (when set in Company Config; user cannot change it)
    const { data: companyTrackerBrandData } = useQuery({
        queryKey: ['brand', 'company-tracker', companyTrackerBrandId],
        queryFn: async () => {
            try {
                const brandData = await apiService.getBrandData(companyTrackerBrandId!);
                if (brandData?.brand) {
                    return {
                        _id: brandData.brand._id,
                        name: brandData.brand.name,
                        category: brandData.brand.category,
                        logoUrl: brandData.brand.logoUrl,
                        s3Key: brandData.brand.s3Key,
                    };
                }
                return null;
            } catch {
                return null;
            }
        },
        enabled: isTracker && !!companyTrackerBrandId,
        staleTime: 10 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
    });

    // Initialize from fixed brand, company tracker brand, URL params (Brand Explorer only), or store
    useEffect(() => {
        if (fixedBrandId && fixedBrandData && fixedBrandData._id !== selectedBrand?._id) {
            setSelectedBrand(fixedBrandData);
        } else if (isTracker && companyTrackerBrandId && companyTrackerBrandData && companyTrackerBrandData._id !== selectedBrand?._id) {
            setSelectedBrand(companyTrackerBrandData);
        } else if (!fixedBrandId && !isTracker) {
            const brandIdParam = searchParams.get('brandId');
            const startParam = searchParams.get('startDate');
            const endParam = searchParams.get('endDate');
            if (brandIdParam && brands.length > 0) {
                const brand = brands.find(b => b._id === brandIdParam);
                if (brand && brand._id !== selectedBrand?._id) {
                    setSelectedBrand(brand);
                }
            }
            if (startParam && endParam) {
                setExplorerStartDate(startParam);
                setExplorerEndDate(endParam);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, brands.length, fixedBrandData, isTracker, companyTrackerBrandId, companyTrackerBrandData]);

    // Update URL when brand or date range changes. Tracker: no query params (brand/date in Zustand).
    useEffect(() => {
        if (fixedBrandId || isTracker) {
            setSearchParams({}, { replace: true });
        } else {
            const params = new URLSearchParams();
            if (selectedBrand) params.set('brandId', selectedBrand._id);
            params.set('startDate', explorerStartDate);
            params.set('endDate', explorerEndDate);
            if (platform) params.set('platform', platform);
            setSearchParams(params, { replace: true });
        }
    }, [selectedBrand, explorerStartDate, explorerEndDate, setSearchParams, fixedBrandId, platform, isTracker]);

    // Tracker: company-assigned brand (when set) or store; Brand Explorer: fixed/URL/store.
    const effectiveBrandId = fixedBrandId || (isTracker ? companyTrackerBrandId : null) || selectedBrand?._id || (isTracker ? null : searchParams.get('brandId'));
    const effectiveStartDate = isTracker ? trackerStartDate : (searchParams.get('startDate') || explorerStartDate);
    const effectiveEndDate = isTracker ? trackerEndDate : (searchParams.get('endDate') || explorerEndDate);

    // Fetch brand data for selected brand and date range (single backend path: PBP + BM)
    const { data: brandData, isLoading: brandDataLoading, error: brandDataError } = useQuery({
        queryKey: ['brand-data', effectiveBrandId, effectiveStartDate, effectiveEndDate],
        queryFn: () => apiService.getBrandData(
            effectiveBrandId!,
            undefined,
            undefined,
            effectiveStartDate,
            effectiveEndDate
        ),
        enabled: !!effectiveBrandId,
        staleTime: 10 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
    });

    // Use all periods returned by API (backend filters by date range when startDate/endDate are passed)
    const currentPeriodData = useMemo(() => {
        if (!brandData?.periods) return null;
        // Optional client-side date filter for TonyBet Jan 2026
        if (fixedBrandId && effectiveStartDate && effectiveEndDate) {
            const filterMentions = (mention: any) => {
                const date = new Date(mention.content?.publishedAt);
                const dateStr = date.toISOString().slice(0, 10);
                return dateStr >= effectiveStartDate && dateStr <= effectiveEndDate;
            };
            return brandData.periods.map((period: any) => {
                const filtered = period.mentions.filter(filterMentions);
                return {
                    ...period,
                    mentions: filtered,
                    programs: period.programs
                        .filter((p: any) => filtered.some((m: any) => m.program?._id === p._id))
                        .map((p: any) => ({ ...p, mentionCount: filtered.filter((m: any) => m.program?._id === p._id).length })),
                    podcasts: period.podcasts
                        .filter((p: any) => filtered.some((m: any) => m.podcast?._id === p._id))
                        .map((p: any) => ({ ...p, mentionCount: filtered.filter((m: any) => m.podcast?._id === p._id).length })),
                };
            });
        }
        return brandData.periods;
    }, [brandData, fixedBrandId, effectiveStartDate, effectiveEndDate]);

    // Split by long form (YouTube video) vs short form (YouTube short / IG / TikTok); backend always sends contentForm
    const longFormYearData = useMemo(() => {
        if (!currentPeriodData) return null;
        return currentPeriodData.map(y => ({
            ...y,
            mentions: y.mentions.filter((m: any) => (m.contentForm || 'long') === 'long'),
        }));
    }, [currentPeriodData]);

    const shortFormYearData = useMemo(() => {
        if (!currentPeriodData) return null;
        return currentPeriodData.map(y => ({
            ...y,
            mentions: y.mentions.filter((m: any) => (m.contentForm || 'short') === 'short'),
        }));
    }, [currentPeriodData]);

    const displayYearDataForPrograms = longFormYearData ?? currentPeriodData;
    const displayYearDataForCreators = shortFormYearData ?? currentPeriodData;

    // Merge all periods into one for the programs table so we show a single table for the whole date range (no duplicate tables per year)
    const mergedProgramsData = useMemo(() => {
        if (!displayYearDataForPrograms?.length) return null;
        const allMentions = displayYearDataForPrograms.flatMap((p: any) => p.mentions ?? []);
        const programIds = new Set<string>();
        displayYearDataForPrograms.forEach((p: any) => (p.programs ?? []).forEach((prog: any) => programIds.add(prog._id?.toString?.() ?? String(prog._id))));
        const programById = new Map<string, any>();
        displayYearDataForPrograms.forEach((p: any) => (p.programs ?? []).forEach((prog: any) => {
            const id = prog._id?.toString?.() ?? String(prog._id);
            if (!programById.has(id)) programById.set(id, { ...prog });
        }));
        const programs = Array.from(programById.values()).map((program: any) => {
            const programMentions = allMentions.filter((m: any) => m.program && (m.program._id?.toString?.() ?? String(m.program._id)) === (program._id?.toString?.() ?? String(program._id)));
            const accumulatedViews = programMentions.reduce((sum: number, mention: any) => {
                const views = mention.content?.views || (mention.content as any)?.insights?.views || 0;
                return sum + views;
            }, 0);
            return { ...program, mentionCount: programMentions.length, accumulatedViews };
        });
        return { year: 0, mentions: allMentions, programs };
    }, [displayYearDataForPrograms]);

    // Livestream programs: same shape as VOD programs table — one row per program with livestream mentions; accumulatedLiveViewers = sum of concurrentViewersAtMention at mention time
    const mergedLivestreamProgramsData = useMemo(() => {
        if (!mergedProgramsData?.mentions) return null;
        const withLivestream = mergedProgramsData.mentions.filter((m: any) => m.content?.livestream && m.program?._id);
        const programIds = new Set<string>();
        withLivestream.forEach((m: any) => programIds.add(m.program._id?.toString?.() ?? String(m.program._id)));
        const programById = new Map<string, any>();
        withLivestream.forEach((m: any) => {
            const id = m.program._id?.toString?.() ?? String(m.program._id);
            if (!programById.has(id)) programById.set(id, { ...m.program, podcast: m.podcast });
        });
        const programs = Array.from(programById.values()).map((program: any) => {
            const programMentions = withLivestream.filter((m: any) => (m.program._id?.toString?.() ?? String(m.program._id)) === (program._id?.toString?.() ?? String(program._id)));
            const accumulatedLiveViewers = programMentions.reduce((sum: number, mention: any) => {
                const v = mention.content?.livestream?.concurrentViewersAtMention;
                return sum + (typeof v === 'number' ? v : 0);
            }, 0);
            return { ...program, mentionCount: programMentions.length, accumulatedLiveViewers };
        });
        return { programs };
    }, [mergedProgramsData]);

    // Calculate the latest metrics collection date from YouTube content
    const latestMetricsDate = useMemo((): Date | null => {
        if (!currentPeriodData) return null;

        const allMentions = currentPeriodData.flatMap(y => y.mentions);
        // Filter for YouTube content only (not Instagram/TikTok)
        const youtubeMentions = allMentions.filter(m =>
            m.content?.source !== 'instagram' && m.content?.source !== 'tiktok'
        );

        let latestDate: Date | null = null;
        youtubeMentions.forEach(mention => {
            const collectedAt = (mention.content as any)?.metricsCollectedAt;
            if (collectedAt) {
                const date = new Date(collectedAt);
                if (!latestDate || date > latestDate) {
                    latestDate = date;
                }
            }
        });

        return latestDate;
    }, [currentPeriodData]);

    // Helper function to get ISO week number
    const getWeekNumber = (date: Date): number => {
        const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
        const dayNum = d.getUTCDay() || 7;
        d.setUTCDate(d.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    };

    const getWeekStart = (date: Date): Date => {
        const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
        const day = d.getUTCDay();
        const diff = d.getUTCDate() - day + (day === 0 ? -6 : 1);
        d.setUTCDate(diff);
        return d;
    };

    // Calculate week-by-week trends
    const weeklyTrends = useMemo(() => {
        if (!currentPeriodData) return [];

        const allMentions = currentPeriodData.flatMap(y => y.mentions);

        // Group by week
        const weekMap = new Map<string, {
            week: string;
            weekStart: Date;
            podcasts: Set<string>;
            programs: Set<string>;
            episodes: Set<string>;
            mentions: number;
            views: number;
            liveViewers: number;
        }>();

        allMentions.forEach(mention => {
            const date = new Date(mention.content.publishedAt);
            const weekStart = getWeekStart(date);
            const weekKey = `${weekStart.getUTCFullYear()}-W${String(getWeekNumber(date)).padStart(2, '0')}`;
            const weekLabel = `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}`;

            if (!weekMap.has(weekKey)) {
                weekMap.set(weekKey, {
                    week: weekLabel,
                    weekStart: weekStart,
                    podcasts: new Set(),
                    programs: new Set(),
                    episodes: new Set(),
                    mentions: 0,
                    views: 0,
                    liveViewers: 0,
                });
            }

            const weekData = weekMap.get(weekKey)!;

            if (mention.podcast?._id) {
                weekData.podcasts.add(mention.podcast._id);
            }
            if (mention.program?._id) {
                weekData.programs.add(mention.program._id);
            }
            if (mention.content?._id) {
                weekData.episodes.add(mention.content._id);
            }
            weekData.mentions += 1;
            weekData.views += mention.content.views || (mention.content as any).insights?.views || 0;
            weekData.liveViewers += (mention.content as any)?.livestream?.concurrentViewersAtMention ?? 0;
        });

        // Convert to array and sort by week
        return Array.from(weekMap.values())
            .sort((a, b) => a.weekStart.getTime() - b.weekStart.getTime())
            .map(data => ({
                week: data.week,
                weekStart: data.weekStart.toISOString(),
                podcasts: data.podcasts.size,
                programs: data.programs.size,
                episodes: data.episodes.size,
                mentions: data.mentions,
                views: data.views,
                liveViewers: data.liveViewers,
            }));
    }, [currentPeriodData]);


    // Short-form mentions from displayYearDataForCreators (only when platform is not 'yt')
    const shortFormMentions = useMemo(() => {
        if (!displayYearDataForCreators || platform === 'yt') return [];
        return displayYearDataForCreators.flatMap(y => y.mentions);
    }, [displayYearDataForCreators, platform]);

    // Split short form into: Instagram Reels, YouTube Shorts, TikTok Videos
    const instagramReelsMentions = useMemo(() =>
        shortFormMentions.filter((m: any) => m.content?.source === 'instagram' && m.content?.mediaType === 'reel'),
        [shortFormMentions]
    );
    const tiktokVideosMentions = useMemo(() =>
        shortFormMentions.filter((m: any) => m.content?.source === 'tiktok'),
        [shortFormMentions]
    );

    // YouTube section from backend (same source for Resumen + table)
    const { youtubeSectionStatistics, youtubeAccountsData } = useMemo(() => {
        const yearsWithYt = currentPeriodData?.filter((y: any) => y.youtubeSection) ?? [];
        if (!yearsWithYt.length) {
            return { youtubeSectionStatistics: null, youtubeAccountsData: [] };
        }
        const stats = yearsWithYt.reduce(
            (acc: any, y: any) => {
                const s = y.youtubeSection!.statistics;
                acc.totalMentions += s.totalMentions;
                acc.uniqueEpisodes += s.uniqueEpisodes;
                acc.uniquePrograms += s.uniquePrograms;
                acc.uniquePodcasts += s.uniquePodcasts;
                acc.accumulatedViews += s.accumulatedViews;
                return acc;
            },
            { totalMentions: 0, uniqueEpisodes: 0, uniquePrograms: 0, uniquePodcasts: 0, accumulatedViews: 0 }
        );
        const byChannel = new Map<string, { channelId: string; channelName: string; imageUrl?: string; imageS3Key?: string; totalShorts: number; totalViews: number; totalLikes: number; totalComments: number }>();
        yearsWithYt.forEach((y: any) => {
            (y.youtubeSection!.youtubeShortsByAccount || []).forEach((a: any) => {
                const existing = byChannel.get(a.channelId);
                if (existing) {
                    existing.totalShorts += a.totalShorts;
                    existing.totalViews += a.totalViews;
                    existing.totalLikes += a.totalLikes;
                    existing.totalComments += a.totalComments;
                } else {
                    byChannel.set(a.channelId, { ...a });
                }
            });
        });
        const youtubeAccountsData = Array.from(byChannel.values()).sort((a, b) => b.totalViews - a.totalViews);
        const youtubeSectionStatistics = (stats.totalMentions > 0 || stats.accumulatedViews > 0 || youtubeAccountsData.length > 0)
            ? stats
            : null;
        return { youtubeSectionStatistics, youtubeAccountsData };
    }, [currentPeriodData]);

    // Legacy: combined social content (Instagram + TikTok) for existing creators table when not in tracker sectioned view
    const socialContent = useMemo(() => {
        if (!displayYearDataForCreators || platform === 'yt') return [];
        return shortFormMentions.filter((mention: any) =>
            (mention.content?.source === 'instagram' && mention.content?.mediaType === 'reel') ||
            (mention.content?.source === 'tiktok' && mention.content?.mediaType === 'video')
        );
    }, [displayYearDataForCreators, platform, shortFormMentions]);

    // Helper function to extract TikTok username from URL
    const extractTikTokUsername = (url: string): string | null => {
        const match = url?.match(/tiktok\.com\/@([^/]+)/);
        return match ? match[1] : null;
    };

    // Group social content by creator and calculate aggregated metrics
    const creatorsData = useMemo(() => {
        if (!socialContent.length) return [];

        const creatorMap = new Map<string, {
            creatorId: string;
            username: string;
            fullName?: string;
            profilePicUrl?: string;
            profilePicS3Key?: string;
            isVerified?: boolean;
            followersCount?: number;
            platform?: 'instagram' | 'tiktok';
            reels: typeof socialContent;
            totalReels: number;
            totalViews: number;
            totalLikes: number;
            totalComments: number;
        }>();

        socialContent.forEach((mention) => {
            const source = mention.content?.source;
            let username: string | undefined;
            let creatorId: string;
            let fullName: string | undefined;
            let profilePicUrl: string | undefined;
            let profilePicS3Key: string | undefined;
            let isVerified: boolean | undefined;
            let followersCount: number | undefined;

            if (source === 'instagram') {
                const owner = mention.content?.insights?.owner;
                const accountMetadata = mention.content?.insights?.accountMetadata;
                if (!owner?.username) return;
                username = owner.username;
                // Always use username for grouping to avoid duplicates (some content has owner.id, some doesn't)
                creatorId = owner.username;
                fullName = owner.fullName;
                profilePicUrl = accountMetadata?.profilePicUrl;
                profilePicS3Key = (accountMetadata as any)?.profilePicS3Key;
                isVerified = owner.isVerified;
                followersCount = accountMetadata?.followersCount;
            } else if (source === 'tiktok') {
                // Extract username from TikTok URL or from insights.owner
                const owner = mention.content?.insights?.owner;
                const accountMetadata = mention.content?.insights?.accountMetadata;
                username = owner?.username || extractTikTokUsername(mention.content?.url || '') || undefined;
                if (!username) return;
                creatorId = username;
                fullName = owner?.fullName;
                profilePicUrl = accountMetadata?.profilePicUrl;
                profilePicS3Key = (accountMetadata as any)?.profilePicS3Key;
                isVerified = owner?.isVerified;
                followersCount = accountMetadata?.followersCount;
            } else {
                return;
            }

            const existing = creatorMap.get(creatorId);

            const views = mention.content?.views || 0;
            const likes = mention.content?.likes || 0;
            const comments = mention.content?.comments || 0;

            if (existing) {
                existing.reels.push(mention);
                existing.totalReels += 1;
                existing.totalViews += views;
                existing.totalLikes += likes;
                existing.totalComments += comments;
                // Update profilePicS3Key if available and not already set
                if (profilePicS3Key && !existing.profilePicS3Key) {
                    existing.profilePicS3Key = profilePicS3Key;
                }
            } else {
                creatorMap.set(creatorId, {
                    creatorId,
                    username: username!,
                    fullName,
                    profilePicUrl,
                    profilePicS3Key,
                    isVerified,
                    followersCount,
                    platform: source as 'instagram' | 'tiktok',
                    reels: [mention],
                    totalReels: 1,
                    totalViews: views,
                    totalLikes: likes,
                    totalComments: comments,
                });
            }
        });

        return Array.from(creatorMap.values()).sort((a, b) => b.totalViews - a.totalViews);
    }, [socialContent, extractTikTokUsername]);

    // Creator-grouped data per short-form type (for sectioned tracker view)
    const buildCreatorsFromMentions = (mentions: any[], platformType: 'instagram' | 'tiktok') => {
        const creatorMap = new Map<string, {
            creatorId: string;
            username: string;
            fullName?: string;
            profilePicUrl?: string;
            profilePicS3Key?: string;
            isVerified?: boolean;
            followersCount?: number;
            platform?: 'instagram' | 'tiktok';
            reels: any[];
            totalReels: number;
            totalViews: number;
            totalLikes: number;
            totalComments: number;
        }>();
        mentions.forEach((mention) => {
            const source = mention.content?.source;
            let username: string | undefined;
            let creatorId: string;
            let fullName: string | undefined;
            let profilePicUrl: string | undefined;
            let profilePicS3Key: string | undefined;
            let isVerified: boolean | undefined;
            let followersCount: number | undefined;
            if (source === 'instagram') {
                const owner = mention.content?.insights?.owner;
                const accountMetadata = mention.content?.insights?.accountMetadata;
                if (!owner?.username) return;
                username = owner.username;
                creatorId = owner.username;
                fullName = owner.fullName;
                profilePicUrl = accountMetadata?.profilePicUrl;
                profilePicS3Key = (accountMetadata as any)?.profilePicS3Key;
                isVerified = owner.isVerified;
                followersCount = accountMetadata?.followersCount;
            } else if (source === 'tiktok') {
                const owner = mention.content?.insights?.owner;
                const accountMetadata = mention.content?.insights?.accountMetadata;
                username = owner?.username || extractTikTokUsername(mention.content?.url || '') || undefined;
                if (!username) return;
                creatorId = username;
                fullName = owner?.fullName;
                profilePicUrl = accountMetadata?.profilePicUrl;
                profilePicS3Key = (accountMetadata as any)?.profilePicS3Key;
                isVerified = owner?.isVerified;
                followersCount = accountMetadata?.followersCount;
            } else return;
            const existing = creatorMap.get(creatorId);
            const views = mention.content?.views || 0;
            const likes = mention.content?.likes || 0;
            const comments = mention.content?.comments || 0;
            if (existing) {
                existing.reels.push(mention);
                existing.totalReels += 1;
                existing.totalViews += views;
                existing.totalLikes += likes;
                existing.totalComments += comments;
                if (profilePicS3Key && !existing.profilePicS3Key) existing.profilePicS3Key = profilePicS3Key;
            } else {
                creatorMap.set(creatorId, {
                    creatorId,
                    username: username!,
                    fullName,
                    profilePicUrl,
                    profilePicS3Key,
                    isVerified,
                    followersCount,
                    platform: platformType,
                    reels: [mention],
                    totalReels: 1,
                    totalViews: views,
                    totalLikes: likes,
                    totalComments: comments,
                });
            }
        });
        return Array.from(creatorMap.values()).sort((a, b) => b.totalViews - a.totalViews);
    };
    // Prefer backend shortFormByCreator (deduped, same as creator detail); fallback to client aggregation
    const creatorsDataInstagram = useMemo(() => {
        const periods = currentPeriodData ?? [];
        const fromBackend = periods.flatMap((p: any) => (p.shortFormByCreator?.instagram ?? []));
        if (fromBackend.length > 0) {
            const merged = new Map<string, { creatorId: string; username: string; profilePicUrl?: string; profilePicS3Key?: string; totalReels: number; totalViews: number; totalLikes: number; totalComments: number }>();
            fromBackend.forEach((c: any) => {
                const existing = merged.get(c.creatorId);
                if (existing) {
                    existing.totalReels += c.totalReels ?? 0;
                    existing.totalViews += c.totalViews ?? 0;
                    existing.totalLikes += c.totalLikes ?? 0;
                    existing.totalComments += c.totalComments ?? 0;
                } else merged.set(c.creatorId, { ...c, totalReels: c.totalReels ?? 0, totalViews: c.totalViews ?? 0, totalLikes: c.totalLikes ?? 0, totalComments: c.totalComments ?? 0 });
            });
            return Array.from(merged.values()).sort((a, b) => b.totalViews - a.totalViews).map(c => ({
                creatorId: c.creatorId,
                username: c.username,
                fullName: undefined,
                profilePicUrl: c.profilePicUrl,
                profilePicS3Key: c.profilePicS3Key,
                isVerified: undefined,
                followersCount: undefined,
                platform: 'instagram' as const,
                totalReels: c.totalReels,
                totalViews: c.totalViews,
                totalLikes: c.totalLikes,
                totalComments: c.totalComments,
                reels: [],
            }));
        }
        return buildCreatorsFromMentions(instagramReelsMentions, 'instagram');
    }, [currentPeriodData, instagramReelsMentions]);

    const creatorsDataTiktok = useMemo(() => {
        const periods = currentPeriodData ?? [];
        const fromBackend = periods.flatMap((p: any) => (p.shortFormByCreator?.tiktok ?? []));
        if (fromBackend.length > 0) {
            const merged = new Map<string, { creatorId: string; username: string; profilePicUrl?: string; profilePicS3Key?: string; totalReels: number; totalViews: number; totalLikes: number; totalComments: number }>();
            fromBackend.forEach((c: any) => {
                const existing = merged.get(c.creatorId);
                if (existing) {
                    existing.totalReels += c.totalReels ?? 0;
                    existing.totalViews += c.totalViews ?? 0;
                    existing.totalLikes += c.totalLikes ?? 0;
                    existing.totalComments += c.totalComments ?? 0;
                } else merged.set(c.creatorId, { ...c, totalReels: c.totalReels ?? 0, totalViews: c.totalViews ?? 0, totalLikes: c.totalLikes ?? 0, totalComments: c.totalComments ?? 0 });
            });
            return Array.from(merged.values()).sort((a, b) => b.totalViews - a.totalViews).map(c => ({
                creatorId: c.creatorId,
                username: c.username,
                fullName: undefined,
                profilePicUrl: c.profilePicUrl,
                profilePicS3Key: c.profilePicS3Key,
                isVerified: undefined,
                followersCount: undefined,
                platform: 'tiktok' as const,
                totalReels: c.totalReels,
                totalViews: c.totalViews,
                totalLikes: c.totalLikes,
                totalComments: c.totalComments,
                reels: [],
            }));
        }
        return buildCreatorsFromMentions(tiktokVideosMentions, 'tiktok');
    }, [currentPeriodData, tiktokVideosMentions]);

    // Calculate combined metrics for all creators
    const combinedCreatorMetrics = useMemo(() => {
        const data = creatorsData as Array<{ totalReels: number; totalViews: number; totalLikes: number; totalComments: number }>;
        if (!data.length) return null;
        return {
            totalCreators: data.length,
            totalReels: data.reduce((sum, creator) => sum + creator.totalReels, 0),
            totalViews: data.reduce((sum, creator) => sum + creator.totalViews, 0),
            totalLikes: data.reduce((sum, creator) => sum + creator.totalLikes, 0),
            totalComments: data.reduce((sum, creator) => sum + creator.totalComments, 0),
        };
    }, [creatorsData]);

    // Short-form summary: Contenido (content where brand appeared), Views, Interacciones, Creadores (creator accounts)
    const shortFormSummary = useMemo(() => {
        if (isTracker) {
            const contenido =
                creatorsDataInstagram.reduce((s, c) => s + c.totalReels, 0) +
                creatorsDataTiktok.reduce((s, c) => s + c.totalReels, 0) +
                youtubeAccountsData.reduce((s, c) => s + c.totalShorts, 0);
            const creators = creatorsDataInstagram.length + creatorsDataTiktok.length + youtubeAccountsData.length;
            const views =
                creatorsDataInstagram.reduce((s, c) => s + c.totalViews, 0) +
                creatorsDataTiktok.reduce((s, c) => s + c.totalViews, 0) +
                youtubeAccountsData.reduce((s, c) => s + c.totalViews, 0);
            const interactions =
                creatorsDataInstagram.reduce((s, c) => s + c.totalLikes + c.totalComments, 0) +
                creatorsDataTiktok.reduce((s, c) => s + c.totalLikes + c.totalComments, 0) +
                youtubeAccountsData.reduce((s, c) => s + c.totalLikes + c.totalComments, 0);
            if (contenido === 0 && views === 0 && interactions === 0 && creators === 0) return null;
            return { contenido, views, interactions, creators };
        }
        if (!combinedCreatorMetrics) return null;
        return {
            contenido: combinedCreatorMetrics.totalReels,
            views: combinedCreatorMetrics.totalViews,
            interactions: combinedCreatorMetrics.totalLikes + combinedCreatorMetrics.totalComments,
            creators: combinedCreatorMetrics.totalCreators,
        };
    }, [isTracker, creatorsDataInstagram, creatorsDataTiktok, youtubeAccountsData, combinedCreatorMetrics]);

    // Calculate week-by-week trends for social content (Instagram & TikTok)
    const socialWeeklyTrends = useMemo(() => {
        if (!socialContent.length) return [];

        // Group by week
        const weekMap = new Map<string, {
            week: string;
            weekStart: Date;
            creators: Set<string>;
            reels: number;
            views: number;
            interactions: number;
        }>();

        socialContent.forEach(mention => {
            const date = new Date(mention.content.publishedAt);
            const weekStart = getWeekStart(date);
            const weekKey = `${weekStart.getUTCFullYear()}-W${String(getWeekNumber(date)).padStart(2, '0')}`;
            const weekLabel = `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}`;

            if (!weekMap.has(weekKey)) {
                weekMap.set(weekKey, {
                    week: weekLabel,
                    weekStart: weekStart,
                    creators: new Set(),
                    reels: 0,
                    views: 0,
                    interactions: 0,
                });
            }

            const weekData = weekMap.get(weekKey)!;

            // Get creator ID
            const source = mention.content?.source;
            let creatorId: string;
            if (source === 'instagram') {
                creatorId = mention.content?.insights?.owner?.id || mention.content?.insights?.owner?.username || 'unknown';
            } else {
                const username = mention.content?.url ? extractTikTokUsername(mention.content.url) : null;
                creatorId = username || 'unknown';
            }

            weekData.creators.add(creatorId);
            weekData.reels += 1;
            weekData.views += mention.content.views || 0;
            weekData.interactions += (mention.content.likes || 0) + (mention.content.comments || 0);
        });

        // Convert to array and sort by week
        return Array.from(weekMap.values())
            .sort((a, b) => a.weekStart.getTime() - b.weekStart.getTime())
            .map(data => ({
                week: data.week,
                weekStart: data.weekStart.toISOString(),
                creators: data.creators.size,
                reels: data.reels,
                views: data.views,
                interactions: data.interactions,
            }));
    }, [socialContent, extractTikTokUsername]);

    // Format number with Spanish format (dots as thousands separator)
    const formatNumber = (num: number): string => {
        return new Intl.NumberFormat('es-ES').format(num);
    };

    // Y-axis / chart compact format: 1K, 3.5K, 1M
    const formatCompact = (num: number): string => {
        if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
        if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
        return String(num);
    };

    // Sorting state for creators table
    const [creatorsOrderBy, setCreatorsOrderBy] = useState<'username' | 'totalReels' | 'totalViews' | 'totalLikes' | 'totalComments' | 'totalInteractions'>('totalViews');
    const [creatorsOrder, setCreatorsOrder] = useState<'asc' | 'desc'>('desc');

    // Handle sort request for creators table
    const handleCreatorsSort = (property: typeof creatorsOrderBy) => {
        const isAsc = creatorsOrderBy === property && creatorsOrder === 'asc';
        setCreatorsOrder(isAsc ? 'desc' : 'asc');
        setCreatorsOrderBy(property);
    };

    // Sorting state for programs table
    const [programsOrderBy, setProgramsOrderBy] = useState<'name' | 'podcast' | 'mentions' | 'views'>('views');
    const [programsOrder, setProgramsOrder] = useState<'asc' | 'desc'>('desc');

    // Sorting state for livestream programs table (same columns as VOD: name, podcast, mentions, viewsEnVivo)
    const [livestreamProgramsOrderBy, setLivestreamProgramsOrderBy] = useState<'name' | 'podcast' | 'mentions' | 'viewsEnVivo'>('viewsEnVivo');
    const [livestreamProgramsOrder, setLivestreamProgramsOrder] = useState<'asc' | 'desc'>('desc');

    // Sorting state for YouTube Shorts table (Formato corto — by account, same as IG/TikTok)
    const [shortFormYoutubeOrderBy, setShortFormYoutubeOrderBy] = useState<'channelName' | 'totalShorts' | 'totalViews' | 'totalLikes' | 'totalComments'>('totalViews');
    const [shortFormYoutubeOrder, setShortFormYoutubeOrder] = useState<'asc' | 'desc'>('desc');

    // Handle sort request for programs table
    const handleProgramsSort = (property: typeof programsOrderBy) => {
        const isAsc = programsOrderBy === property && programsOrder === 'asc';
        setProgramsOrder(isAsc ? 'desc' : 'asc');
        setProgramsOrderBy(property);
    };

    // Handle sort request for livestream programs table
    const handleLivestreamProgramsSort = (property: typeof livestreamProgramsOrderBy) => {
        const isAsc = livestreamProgramsOrderBy === property && livestreamProgramsOrder === 'asc';
        setLivestreamProgramsOrder(isAsc ? 'desc' : 'asc');
        setLivestreamProgramsOrderBy(property);
    };

    // Sorted creators data
    const sortedCreatorsData = useMemo(() => {
        if (!creatorsData.length) return [];

        const sorted = [...creatorsData].sort((a, b) => {
            let aValue: number | string;
            let bValue: number | string;

            switch (creatorsOrderBy) {
                case 'username':
                    aValue = a.username.toLowerCase();
                    bValue = b.username.toLowerCase();
                    break;
                case 'totalReels':
                    aValue = a.totalReels;
                    bValue = b.totalReels;
                    break;
                case 'totalViews':
                    aValue = a.totalViews;
                    bValue = b.totalViews;
                    break;
                case 'totalLikes':
                    aValue = a.totalLikes;
                    bValue = b.totalLikes;
                    break;
                case 'totalComments':
                    aValue = a.totalComments;
                    bValue = b.totalComments;
                    break;
                case 'totalInteractions':
                    aValue = a.totalLikes + a.totalComments;
                    bValue = b.totalLikes + b.totalComments;
                    break;
                default:
                    return 0;
            }

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                if (aValue < bValue) return creatorsOrder === 'asc' ? -1 : 1;
                if (aValue > bValue) return creatorsOrder === 'asc' ? 1 : -1;
                return 0;
            }

            if (aValue < bValue) return creatorsOrder === 'asc' ? -1 : 1;
            if (aValue > bValue) return creatorsOrder === 'asc' ? 1 : -1;
            return 0;
        });

        return sorted;
    }, [creatorsData, creatorsOrderBy, creatorsOrder]);

    // Sorted creators for Formato corto (per-platform tables): same sort logic as sortedCreatorsData
    const sortedCreatorsDataInstagram = useMemo(() => {
        if (!creatorsDataInstagram.length) return [];
        const sorted = [...creatorsDataInstagram].sort((a, b) => {
            let aValue: number | string;
            let bValue: number | string;
            switch (creatorsOrderBy) {
                case 'username': aValue = a.username.toLowerCase(); bValue = b.username.toLowerCase(); break;
                case 'totalReels': aValue = a.totalReels; bValue = b.totalReels; break;
                case 'totalViews': aValue = a.totalViews; bValue = b.totalViews; break;
                case 'totalLikes': aValue = a.totalLikes; bValue = b.totalLikes; break;
                case 'totalComments': aValue = a.totalComments; bValue = b.totalComments; break;
                case 'totalInteractions': aValue = a.totalLikes + a.totalComments; bValue = b.totalLikes + b.totalComments; break;
                default: return 0;
            }
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                if (aValue < bValue) return creatorsOrder === 'asc' ? -1 : 1;
                if (aValue > bValue) return creatorsOrder === 'asc' ? 1 : -1;
                return 0;
            }
            if (aValue < bValue) return creatorsOrder === 'asc' ? -1 : 1;
            if (aValue > bValue) return creatorsOrder === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [creatorsDataInstagram, creatorsOrderBy, creatorsOrder]);
    const sortedCreatorsDataTiktok = useMemo(() => {
        if (!creatorsDataTiktok.length) return [];
        const sorted = [...creatorsDataTiktok].sort((a, b) => {
            let aValue: number | string;
            let bValue: number | string;
            switch (creatorsOrderBy) {
                case 'username': aValue = a.username.toLowerCase(); bValue = b.username.toLowerCase(); break;
                case 'totalReels': aValue = a.totalReels; bValue = b.totalReels; break;
                case 'totalViews': aValue = a.totalViews; bValue = b.totalViews; break;
                case 'totalLikes': aValue = a.totalLikes; bValue = b.totalLikes; break;
                case 'totalComments': aValue = a.totalComments; bValue = b.totalComments; break;
                case 'totalInteractions': aValue = a.totalLikes + a.totalComments; bValue = b.totalLikes + b.totalComments; break;
                default: return 0;
            }
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                if (aValue < bValue) return creatorsOrder === 'asc' ? -1 : 1;
                if (aValue > bValue) return creatorsOrder === 'asc' ? 1 : -1;
                return 0;
            }
            if (aValue < bValue) return creatorsOrder === 'asc' ? -1 : 1;
            if (aValue > bValue) return creatorsOrder === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [creatorsDataTiktok, creatorsOrderBy, creatorsOrder]);

    const sortedYoutubeAccountsData = useMemo(() => {
        if (!youtubeAccountsData.length) return [];
        return [...youtubeAccountsData].sort((a, b) => {
            let aValue: number | string;
            let bValue: number | string;
            switch (shortFormYoutubeOrderBy) {
                case 'channelName': aValue = a.channelName.toLowerCase(); bValue = b.channelName.toLowerCase(); break;
                case 'totalShorts': aValue = a.totalShorts; bValue = b.totalShorts; break;
                case 'totalViews': aValue = a.totalViews; bValue = b.totalViews; break;
                case 'totalLikes': aValue = a.totalLikes; bValue = b.totalLikes; break;
                case 'totalComments': aValue = a.totalComments; bValue = b.totalComments; break;
                default: return 0;
            }
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                if (aValue < bValue) return shortFormYoutubeOrder === 'asc' ? -1 : 1;
                if (aValue > bValue) return shortFormYoutubeOrder === 'asc' ? 1 : -1;
                return 0;
            }
            if (aValue < bValue) return shortFormYoutubeOrder === 'asc' ? -1 : 1;
            if (aValue > bValue) return shortFormYoutubeOrder === 'asc' ? 1 : -1;
            return 0;
        });
    }, [youtubeAccountsData, shortFormYoutubeOrderBy, shortFormYoutubeOrder]);

    const handleShortFormYoutubeSort = (property: typeof shortFormYoutubeOrderBy) => {
        const isAsc = shortFormYoutubeOrderBy === property && shortFormYoutubeOrder === 'asc';
        setShortFormYoutubeOrder(isAsc ? 'desc' : 'asc');
        setShortFormYoutubeOrderBy(property);
    };

    const handleProgramClick = (programId: string, year: number) => {
        if (selectedBrand) {
            const programRoute = fixedBrandId
                ? ROUTES_NAMES.TONYBET_EXPLORER_PROGRAM
                : ROUTES_NAMES.TRACKER_PROGRAM;
            const route = programRoute.replace(':programId', programId);
            if (isTracker) {
                navigate(route);
            } else {
                const params = new URLSearchParams();
                params.set('brandId', selectedBrand._id);
                params.set('startDate', effectiveStartDate);
                params.set('endDate', effectiveEndDate);
                if (platform) params.set('platform', platform);
                navigate(`${route}?${params.toString()}`);
            }
        }
    };

    const handleLivestreamProgramClick = (programId: string) => {
        if (selectedBrand && isTracker) {
            const route = ROUTES_NAMES.TRACKER_LIVESTREAM_PROGRAM.replace(':programId', programId);
            navigate(route);
        }
    };

    const handleCreatorClick = (creatorId: string, _year: number, platformKey?: 'instagram' | 'tiktok') => {
        if (selectedBrand) {
            const creatorRoute = fixedBrandId
                ? ROUTES_NAMES.TONYBET_EXPLORER_CREATOR
                : ROUTES_NAMES.TRACKER_CREATOR;
            const route = fixedBrandId || isTracker
                ? creatorRoute.replace(':platform', platformKey === 'instagram' || platformKey === 'tiktok' ? platformKey : 'instagram').replace(':creatorId', creatorId)
                : creatorRoute.replace(':creatorId', creatorId);
            if (fixedBrandId || isTracker) {
                navigate(route);
            } else {
                const params = new URLSearchParams();
                params.set('brandId', selectedBrand._id);
                params.set('startDate', effectiveStartDate);
                params.set('endDate', effectiveEndDate);
                if (platform) params.set('platform', platform);
                navigate(`${route}?${params.toString()}`);
            }
        }
    };

    const handleYoutubeShortsCreatorClick = (channelId: string) => {
        if (!isTracker) return;
        const route = ROUTES_NAMES.TRACKER_YOUTUBE_SHORTS_CREATOR.replace(':channelId', channelId);
        navigate(route);
    };

    const prefetchProgramDetail = (programId: string, _year: number) => {
        scheduleHoverPrefetch(() => {
            if (selectedBrand) {
                const sd = isTracker ? trackerStartDate : effectiveStartDate;
                const ed = isTracker ? trackerEndDate : effectiveEndDate;
                queryClient.prefetchQuery({
                    queryKey: ['brand-program-detail', selectedBrand._id, programId, undefined, sd, ed],
                    queryFn: () => apiService.getBrandProgramDetail(selectedBrand._id, programId, undefined, sd, ed),
                    staleTime: 10 * 60 * 1000,
                });
            }
        });
    };

    const prefetchCreatorDetail = (creatorId: string, _year: number, platformKey?: 'instagram' | 'tiktok') => {
        scheduleHoverPrefetch(() => {
            if (selectedBrand) {
                const sd = isTracker ? trackerStartDate : effectiveStartDate;
                const ed = isTracker ? trackerEndDate : effectiveEndDate;
                const apiPlatform = platformKey === 'instagram' || platformKey === 'tiktok' ? platformKey : undefined;
                queryClient.prefetchQuery({
                    queryKey: ['brand-creator-detail', selectedBrand._id, creatorId, undefined, apiPlatform, sd, ed],
                    queryFn: () => apiService.getBrandCreatorDetail(selectedBrand._id, creatorId, undefined, undefined, sd, ed, apiPlatform),
                    staleTime: 10 * 60 * 1000,
                });
            }
        });
    };

    const handleBreadcrumbClick = () => {
        reset();
        setSearchParams({}, { replace: true });
    };

    // For fixed brand (public route), show data even without year selection
    // For regular route, require both brand and year
    // Use effectiveBrandId for TonyBet to avoid waiting for selectedBrand state
    const hasSelection = fixedBrandId
        ? effectiveBrandId && brandData
        : (isTracker ? !!effectiveBrandId : !!selectedBrand);

    // Determine if we're in initial loading state
    // For Tracker, don't block - the brand picker should show immediately
    const isInitialLoading = isTracker
        ? false
        : fixedBrandId
            ? (fixedBrandLoading || brandDataLoading)
            : (activeBrandsLoading || (selectedBrand && brandDataLoading));

    // Show single loading spinner on initial render
    if (isInitialLoading) {
        return (
            <>
                {fixedBrandId && <PodscopePublicHeader />}
                <Container
                    maxWidth="xl"
                    disableGutters
                    sx={{
                        mt: fixedBrandId ? { xs: 10, sm: 11, md: 12 } : 4,
                        mb: 4,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '50vh',
                    }}
                >
                    <CircularProgress />
                </Container>
            </>
        );
    }

    return (
        <>
            {fixedBrandId && <PodscopePublicHeader />}
            <Container
                maxWidth="xl"
                disableGutters={!isTracker}
                sx={{
                    mt: fixedBrandId ? { xs: 10, sm: 11, md: 12 } : isTracker ? 2 : 4,
                    mb: 4,
                    px: isTracker ? undefined : { xs: 2, sm: 3, md: 4 },
                }}
            >
                {/* Tracker mode - ExplorerProduct-like layout */}
                {isTracker && (
                    <>
                        <Box sx={{ mt: 2, mb: 2 }}>
                            <Typography variant="h1" component="h1" gutterBottom>
                                Tracker
                            </Typography>
                        </Box>

                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            alignItems={{ xs: 'stretch', md: 'center' }}
                            justifyContent="space-between"
                            spacing={2}
                            sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, pb: 2 }}
                        >
                            {companyTrackerBrandId ? (
                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                    {(() => {
                                        const brand = companyTrackerBrandData ?? selectedBrand;
                                        if (brand?.s3Key) {
                                            return (
                                                <Avatar
                                                    src={ensureDirectS3Url(getS3Url(brand.s3Key))}
                                                    sx={{ width: 28, height: 28 }}
                                                    alt={brand.name}
                                                />
                                            );
                                        }
                                        if (brand?.logoUrl) {
                                            return (
                                                <Avatar
                                                    src={ensureDirectS3Url(brand.logoUrl)}
                                                    sx={{ width: 28, height: 28 }}
                                                    alt={brand.name}
                                                />
                                            );
                                        }
                                        return (
                                            <Avatar sx={{ width: 28, height: 28, bgcolor: 'action.selected' }}>
                                                <BusinessIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                            </Avatar>
                                        );
                                    })()}
                                    <Typography variant="body1" fontWeight={500}>
                                        {(companyTrackerBrandData ?? selectedBrand)?.name ?? '…'}
                                    </Typography>
                                </Stack>
                            ) : (
                                <Box sx={{ minWidth: 200, maxWidth: 400, flex: 1 }}>
                                    <Autocomplete
                                        options={brands}
                                        getOptionLabel={(option) => option.name}
                                        value={selectedBrand}
                                        inputValue={trackerBrandSearch}
                                        onInputChange={(_, value) => setTrackerBrandSearch(value ?? '')}
                                        onChange={(_, newValue) => {
                                            setSelectedBrand(newValue);
                                            if (newValue) setTrackerBrandSearch('');
                                        }}
                                        loading={trackerBrandsLoading}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Seleccionar Marca"
                                                placeholder="Buscar una marca..."
                                                InputProps={{
                                                    ...params.InputProps,
                                                    startAdornment: selectedBrand ? (
                                                        <Box component="span" sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                                                            {selectedBrand.s3Key ? (
                                                                <Avatar
                                                                    src={ensureDirectS3Url(getS3Url(selectedBrand.s3Key))}
                                                                    sx={{ width: 24, height: 24, mr: 1 }}
                                                                />
                                                            ) : (
                                                                <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                                            )}
                                                            {params.InputProps.startAdornment}
                                                        </Box>
                                                    ) : params.InputProps.startAdornment,
                                                }}
                                            />
                                        )}
                                        renderOption={(props, option) => (
                                            <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {option.s3Key ? (
                                                    <Avatar
                                                        src={ensureDirectS3Url(getS3Url(option.s3Key))}
                                                        sx={{ width: 24, height: 24 }}
                                                    />
                                                ) : (
                                                    <BusinessIcon sx={{ color: 'text.secondary' }} />
                                                )}
                                                <Typography>{option.name}</Typography>
                                                {option.category && (
                                                    <Chip label={option.category} size="small" sx={{ ml: 'auto' }} />
                                                )}
                                            </Box>
                                        )}
                                    />
                                </Box>
                            )}
                            {(companyTrackerBrandId || selectedBrand) && (
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <TextField
                                        size="small"
                                        type="date"
                                        label="Desde"
                                        value={trackerStartDate}
                                        onChange={(e) => setTrackerStartDate(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{
                                            min: TRACKER_MIN_DATE,
                                            max: trackerEndDate,
                                        }}
                                        sx={{ minWidth: 160 }}
                                    />
                                    <TextField
                                        size="small"
                                        type="date"
                                        label="Hasta"
                                        value={trackerEndDate}
                                        onChange={(e) => setTrackerEndDate(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{
                                            min: trackerStartDate,
                                            max: TRACKER_MAX_DATE,
                                        }}
                                        sx={{ minWidth: 160 }}
                                    />
                                </Stack>
                            )}
                        </Stack>

                        {companyTrackerBrandId && !effectiveBrandId && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                                <CircularProgress />
                            </Box>
                        )}

                        {effectiveBrandId && brandDataLoading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                                <CircularProgress />
                            </Box>
                        )}

                        {effectiveBrandId && brandDataError && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                Error al cargar datos. Intenta de nuevo.
                            </Alert>
                        )}

                        {effectiveBrandId && !brandDataLoading && !brandDataError && !brandData?.periods?.length && (
                            <Alert severity="info">
                                No hay datos disponibles para la marca seleccionada.
                            </Alert>
                        )}
                    </>
                )}

                {/* Breadcrumb Navigation - Only show for non-tracker, non-fixed-brand */}
                {!fixedBrandId && !isTracker && (
                    <Breadcrumbs
                        aria-label="breadcrumb"
                        separator={<ChevronRightIcon fontSize="small" />}
                        sx={{ mb: 2 }}
                    >
                        <Link
                            component={RouterLink}
                            to={fixedBrandId ? ROUTES_NAMES.TONYBET_EXPLORER_YEAR : ROUTES_NAMES.TRACKER}
                            onClick={(e) => {
                                e.preventDefault();
                                handleBreadcrumbClick();
                            }}
                            underline="hover"
                            color="inherit"
                            sx={{ cursor: 'pointer' }}
                        >
                            {fixedBrandId ? (isEnglish ? 'TonyBet' : 'TonyBet') : 'Tracker'}
                        </Link>
                        {selectedBrand && (
                            <Link
                                component={RouterLink}
                                to={fixedBrandId ? ROUTES_NAMES.TONYBET_EXPLORER_YEAR : ROUTES_NAMES.TRACKER}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleBreadcrumbClick();
                                }}
                                underline="hover"
                                color={hasSelection ? 'inherit' : 'text.primary'}
                                sx={{ cursor: hasSelection ? 'pointer' : 'default' }}
                            >
                                {selectedBrand.name}
                            </Link>
                        )}
                        {effectiveStartDate && effectiveEndDate && (
                            <Typography color="text.primary">
                                {effectiveStartDate} – {effectiveEndDate}
                            </Typography>
                        )}
                    </Breadcrumbs>
                )}

                {/* Show selection UI when brand or year is not selected (brand-explorer mode only) */}
                {!hasSelection && !fixedBrandId && !isTracker && (
                    <>
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h4" component="h1" gutterBottom>
                                {fixedBrandId ? 'TonyBet' : 'Tracker'}
                            </Typography>
                            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                                {isEnglish
                                    ? 'Select a brand and date range to explore its data - view podcasts, programs and mentions with YouTube links'
                                    : 'Selecciona una marca y rango de fechas para explorar sus datos - ver podcasts, programas y menciones con enlaces de YouTube'}
                            </Typography>
                        </Box>

                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                    <Box>
                                        <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                                            {isEnglish ? 'Step 1: Select Brand' : 'Paso 1: Seleccionar Marca'}
                                        </Typography>
                                        <Autocomplete
                                            options={brands}
                                            getOptionLabel={(option) => option.name}
                                            value={selectedBrand}
                                            onChange={(_, newValue) => {
                                                setSelectedBrand(newValue);
                                            }}
                                            loading={brandsLoading}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={isEnglish ? 'Select Brand' : 'Seleccionar Marca'}
                                                    placeholder={isEnglish ? 'Search for a brand...' : 'Buscar una marca...'}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        startAdornment: selectedBrand ? (
                                                            <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                                                                {selectedBrand.s3Key ? (
                                                                    <Avatar
                                                                        src={ensureDirectS3Url(getS3Url(selectedBrand.s3Key))}
                                                                        sx={{ width: 24, height: 24, mr: 1 }}
                                                                    />
                                                                ) : (
                                                                    <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                                                )}
                                                            </Box>
                                                        ) : null,
                                                    }}
                                                />
                                            )}
                                            renderOption={(props, option) => (
                                                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {option.s3Key ? (
                                                        <Avatar
                                                            src={ensureDirectS3Url(getS3Url(option.s3Key))}
                                                            sx={{ width: 24, height: 24 }}
                                                        />
                                                    ) : (
                                                        <BusinessIcon sx={{ color: 'text.secondary' }} />
                                                    )}
                                                    <Typography>{option.name}</Typography>
                                                    {option.category && (
                                                        <Chip label={option.category} size="small" sx={{ ml: 'auto' }} />
                                                    )}
                                                </Box>
                                            )}
                                        />
                                    </Box>
                                    {selectedBrand && (
                                        <Box>
                                            <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                                                {isEnglish ? 'Step 2: Date range' : 'Paso 2: Rango de fechas'}
                                            </Typography>
                                            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                                                <TextField
                                                    size="small"
                                                    type="date"
                                                    label={isEnglish ? 'From' : 'Desde'}
                                                    value={explorerStartDate}
                                                    onChange={(e) => setExplorerStartDate(e.target.value)}
                                                    InputLabelProps={{ shrink: true }}
                                                    inputProps={{ max: explorerEndDate }}
                                                    sx={{ minWidth: 160 }}
                                                />
                                                <TextField
                                                    size="small"
                                                    type="date"
                                                    label={isEnglish ? 'To' : 'Hasta'}
                                                    value={explorerEndDate}
                                                    onChange={(e) => setExplorerEndDate(e.target.value)}
                                                    InputLabelProps={{ shrink: true }}
                                                    inputProps={{ min: explorerStartDate }}
                                                    sx={{ minWidth: 160 }}
                                                />
                                            </Stack>
                                        </Box>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>

                        {brandDataError && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                Failed to load brand data. Please try again.
                            </Alert>
                        )}

                        {selectedBrand && !brandDataLoading && !brandDataError && !brandData?.periods?.length && (
                            <Alert severity="info">
                                {isEnglish ? 'No data available for the selected brand.' : 'No hay datos disponibles para la marca seleccionada.'}
                            </Alert>
                        )}
                    </>
                )}

                {/* Show data when both brand and year are selected */}
                {hasSelection && selectedBrand && !(isTracker && brandDataLoading) && (
                    <>
                        {!isTracker && (
                            <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
                                {selectedBrand.s3Key ? (
                                    <Avatar
                                        src={ensureDirectS3Url(getS3Url(selectedBrand.s3Key))}
                                        sx={{ width: 56, height: 56 }}
                                    />
                                ) : (
                                    <Avatar sx={{ width: 56, height: 56 }}>
                                        <BusinessIcon />
                                    </Avatar>
                                )}
                                <Box>
                                    <Typography variant="h1" component="h1" sx={{ fontSize: '2rem', fontWeight: 600, mb: 0.5 }}>
                                        {fixedBrandId ? 'TonyBet Chile' : `${selectedBrand.name} - ${effectiveStartDate} – ${effectiveEndDate}`}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {/* Error State */}
                        {brandDataError && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                Failed to load brand data. Please try again.
                            </Alert>
                        )}

                        {/* Data Display */}
                        {!brandDataLoading && !brandDataError && currentPeriodData && (
                            <>
                                {/* YouTube Resumen — same cards, Canales replaced by Espectadores en vivo */}
                                {youtubeSectionStatistics && platform !== 'ig' && (
                                    <Box sx={{ mb: 4 }}>
                                        <Typography variant="h1" component="h1" sx={{ fontSize: '1.8rem', fontWeight: 600, mb: 3 }}>
                                            {isEnglish ? 'Long form' : 'Contenido long-form'}
                                        </Typography>
                                        {latestMetricsDate && (
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                {isEnglish
                                                    ? `Views data as of ${latestMetricsDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                                                    : `Datos de vistas al ${latestMetricsDate.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}`
                                                }
                                            </Typography>
                                        )}
                                        <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                                            {isEnglish ? 'Summary' : 'Resumen'}
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                                                <Card variant="outlined">
                                                    <CardContent>
                                                        <Typography variant="h4" color="primary">
                                                            {formatNumber(youtubeSectionStatistics.accumulatedViews)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Views
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                                                <Card variant="outlined">
                                                    <CardContent>
                                                        <Typography variant="h4" color="primary">
                                                            {formatNumber((mergedLivestreamProgramsData?.programs ?? []).reduce((s: number, p: any) => s + (p.accumulatedLiveViewers ?? 0), 0))}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {isEnglish ? 'Live views' : 'Esp. en vivo'}
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                                                <Card variant="outlined">
                                                    <CardContent>
                                                        <Typography variant="h4" color="primary">
                                                            {formatNumber(youtubeSectionStatistics.totalMentions)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {isEnglish ? 'Mentions' : 'Menciones'}
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                                                <Card variant="outlined">
                                                    <CardContent>
                                                        <Typography variant="h4" color="primary">
                                                            {formatNumber(youtubeSectionStatistics.uniqueEpisodes)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {isEnglish ? 'Episodes' : 'Episodios'}
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                                                <Card variant="outlined">
                                                    <CardContent>
                                                        <Typography variant="h4" color="primary">
                                                            {formatNumber(youtubeSectionStatistics.uniquePrograms)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {isEnglish ? 'Programs' : 'Programas'}
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                )}

                                {/* Weekly Trends — same charts, Canales replaced by Espectadores en vivo */}
                                {weeklyTrends.length > 0 && platform !== 'ig' && (
                                    <Box sx={{ mb: 5 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                                            {isEnglish ? 'Weekly Trends' : 'Tendencias Semanales'}
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid size={{ xs: 6, lg: 'grow' }}>
                                                <TrendChartCard
                                                    title="Views"
                                                    dataKey="views"
                                                    color="#1976d2"
                                                    tooltipLabel="Views"
                                                    granularityTooltip={isEnglish ? 'Week' : 'Semana'}
                                                    data={weeklyTrends}
                                                    median={calculateMedian(weeklyTrends.map(t => t.views))}
                                                    formatValue={formatCompact}
                                                    yAxisWidth={45}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 6, lg: 'grow' }}>
                                                <TrendChartCard
                                                    title={isEnglish ? 'Views en vivo' : 'Esp. en vivo'}
                                                    dataKey="liveViewers"
                                                    color="#0288d1"
                                                    tooltipLabel={isEnglish ? 'Views en vivo' : 'Esp. en vivo'}
                                                    granularityTooltip={isEnglish ? 'Week' : 'Semana'}
                                                    data={weeklyTrends}
                                                    median={calculateMedian(weeklyTrends.map(t => (t as any).liveViewers ?? 0))}
                                                    formatValue={formatCompact}
                                                    allowDecimals={false}
                                                    yAxisWidth={45}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 6, lg: 'grow' }}>
                                                <TrendChartCard
                                                    title={isEnglish ? 'Mentions' : 'Menciones'}
                                                    dataKey="mentions"
                                                    color="#ed6c02"
                                                    tooltipLabel={isEnglish ? 'Mentions' : 'Menciones'}
                                                    granularityTooltip={isEnglish ? 'Week' : 'Semana'}
                                                    data={weeklyTrends}
                                                    median={calculateMedian(weeklyTrends.map(t => t.mentions))}
                                                    allowDecimals={false}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 6, lg: 'grow' }}>
                                                <TrendChartCard
                                                    title={isEnglish ? 'Episodes' : 'Episodios'}
                                                    dataKey="episodes"
                                                    color="#9c27b0"
                                                    tooltipLabel={isEnglish ? 'Episodes' : 'Episodios'}
                                                    granularityTooltip={isEnglish ? 'Week' : 'Semana'}
                                                    data={weeklyTrends}
                                                    median={calculateMedian(weeklyTrends.map(t => t.episodes))}
                                                    allowDecimals={false}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 6, lg: 'grow' }}>
                                                <TrendChartCard
                                                    title={isEnglish ? 'Programs' : 'Programas'}
                                                    dataKey="programs"
                                                    color="#2e7d32"
                                                    tooltipLabel={isEnglish ? 'Programs' : 'Programas'}
                                                    granularityTooltip={isEnglish ? 'Week' : 'Semana'}
                                                    data={weeklyTrends}
                                                    median={calculateMedian(weeklyTrends.map(t => t.programs))}
                                                    allowDecimals={false}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                )}

                                {isTracker && (
                                    <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary' }}>
                                        {isEnglish ? 'YouTube VOD programs' : 'Programas VOD YouTube'}
                                    </Typography>
                                )}
                                {mergedProgramsData && (() => {
                                    const yearData = mergedProgramsData;
                                    // Filter programs by platform if active
                                    const programsWithViews = yearData.programs
                                        .filter((program: any) => {
                                            if (!platform) return true;
                                            const hasMatchingMention = yearData.mentions.some((m: any) =>
                                                m.program && (m.program._id?.toString?.() ?? m.program._id) === (program._id?.toString?.() ?? program._id)
                                            );
                                            return hasMatchingMention;
                                        });

                                    // Sort programs based on current sort state
                                    const sortedPrograms = [...programsWithViews].sort((a, b) => {
                                        let aValue: number | string;
                                        let bValue: number | string;

                                        switch (programsOrderBy) {
                                            case 'name':
                                                aValue = a.name.toLowerCase();
                                                bValue = b.name.toLowerCase();
                                                break;
                                            case 'podcast':
                                                aValue = a.podcast?.name?.toLowerCase() || '';
                                                bValue = b.podcast?.name?.toLowerCase() || '';
                                                break;
                                            case 'mentions':
                                                aValue = a.mentionCount ?? 0;
                                                bValue = b.mentionCount ?? 0;
                                                break;
                                            case 'views':
                                            default:
                                                aValue = a.accumulatedViews ?? 0;
                                                bValue = b.accumulatedViews ?? 0;
                                                break;
                                        }

                                        if (typeof aValue === 'string' && typeof bValue === 'string') {
                                            if (aValue < bValue) return programsOrder === 'asc' ? -1 : 1;
                                            if (aValue > bValue) return programsOrder === 'asc' ? 1 : -1;
                                            return 0;
                                        }

                                        if (aValue < bValue) return programsOrder === 'asc' ? -1 : 1;
                                        if (aValue > bValue) return programsOrder === 'asc' ? 1 : -1;
                                        return 0;
                                    });

                                    // Don't show programs table if platform is 'ig' (only show Instagram section)
                                    if (platform === 'ig') {
                                        return null;
                                    }

                                    return (
                                        <Box key="programs-vod" sx={{ mb: 5 }}>
                                            <TableContainer component={Paper} variant="outlined">
                                                <Box
                                                    sx={{
                                                        display: { xs: 'flex', md: 'none' },
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        px: 2,
                                                        py: 1,
                                                        color: 'text.secondary',
                                                        borderBottom: '1px solid',
                                                        borderColor: 'divider',
                                                    }}
                                                >
                                                    <SwipeLeftIcon />
                                                    <Typography variant="caption">{isEnglish ? 'Swipe to see more' : 'Desliza para ver más'}</Typography>
                                                </Box>
                                                <Table>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={{ padding: { xs: '12px', sm: '16px' } }}>
                                                                <TableSortLabel
                                                                    active={programsOrderBy === 'name'}
                                                                    direction={programsOrderBy === 'name' ? programsOrder : 'asc'}
                                                                    onClick={() => handleProgramsSort('name')}
                                                                >
                                                                    {isEnglish ? 'Program' : 'Programa'}
                                                                </TableSortLabel>
                                                            </TableCell>
                                                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                                                <TableSortLabel
                                                                    active={programsOrderBy === 'podcast'}
                                                                    direction={programsOrderBy === 'podcast' ? programsOrder : 'asc'}
                                                                    onClick={() => handleProgramsSort('podcast')}
                                                                >
                                                                    {isEnglish ? 'Channel' : 'Canal'}
                                                                </TableSortLabel>
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ padding: { xs: '12px', sm: '16px' } }}>
                                                                <TableSortLabel
                                                                    active={programsOrderBy === 'mentions'}
                                                                    direction={programsOrderBy === 'mentions' ? programsOrder : 'desc'}
                                                                    onClick={() => handleProgramsSort('mentions')}
                                                                >
                                                                    {isEnglish ? 'Mentions' : 'Menciones'}
                                                                </TableSortLabel>
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ padding: { xs: '12px', sm: '16px' } }}>
                                                                <TableSortLabel
                                                                    active={programsOrderBy === 'views'}
                                                                    direction={programsOrderBy === 'views' ? programsOrder : 'desc'}
                                                                    onClick={() => handleProgramsSort('views')}
                                                                >
                                                                    Views
                                                                </TableSortLabel>
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ width: { xs: '24px', sm: '40px' }, padding: { xs: '8px 4px', sm: '16px' } }}>
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {sortedPrograms.map((program) => (
                                                            <TableRow
                                                                key={program._id}
                                                                hover
                                                                onClick={() => handleProgramClick(program._id, 0)}
                                                                onMouseEnter={() => prefetchProgramDetail(program._id, 0)}
                                                                sx={{ cursor: 'pointer' }}
                                                            >
                                                                <TableCell sx={{ padding: { xs: '12px', sm: '16px' } }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        {program.imageS3Key ? (
                                                                            <Avatar
                                                                                src={ensureDirectS3Url(getS3Url(program.imageS3Key))}
                                                                                sx={{ width: 32, height: 32 }}
                                                                            />
                                                                        ) : (
                                                                            <Avatar sx={{ width: 32, height: 32 }}>
                                                                                <PlayArrowIcon />
                                                                            </Avatar>
                                                                        )}
                                                                        <Typography variant="body2" fontWeight="medium">
                                                                            {program.name}
                                                                        </Typography>
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                                                    {program.podcast ? (
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            {program.podcast.imageS3Key ? (
                                                                                <Avatar
                                                                                    src={ensureDirectS3Url(getS3Url(program.podcast.imageS3Key))}
                                                                                    sx={{ width: 24, height: 24 }}
                                                                                />
                                                                            ) : (
                                                                                <PodcastsIcon fontSize="small" />
                                                                            )}
                                                                            <Typography variant="body2">
                                                                                {program.podcast.title}
                                                                            </Typography>
                                                                        </Box>
                                                                    ) : (
                                                                        <Typography variant="body2" color="text.secondary">
                                                                            N/A
                                                                        </Typography>
                                                                    )}
                                                                </TableCell>
                                                                <TableCell align="right" sx={{ padding: { xs: '12px', sm: '16px' } }}>
                                                                    <Chip label={program.mentionCount} size="small" />
                                                                </TableCell>
                                                                <TableCell align="right" sx={{ padding: { xs: '12px', sm: '16px' } }}>
                                                                    <Typography variant="body2" fontWeight="medium">
                                                                        {formatNumber(program.accumulatedViews)}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell align="center" sx={{ width: { xs: '24px', sm: '40px' }, padding: { xs: '8px 4px', sm: '16px' } }}>
                                                                    <ChevronRightIcon sx={{ color: 'text.secondary', fontSize: { xs: '1rem', sm: '1.25rem' } }} />
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    );
                                })()}

                                {/* YouTube Livestream programs: same table and behavior as VOD — Program, Channel, Menciones, Views en vivo (sum of concurrent viewers at mention time) */}
                                {isTracker && mergedLivestreamProgramsData && (() => {
                                    const livestreamPrograms = mergedLivestreamProgramsData.programs;
                                    const sortedLivestreamPrograms = [...livestreamPrograms].sort((a, b) => {
                                        let aValue: number | string;
                                        let bValue: number | string;
                                        switch (livestreamProgramsOrderBy) {
                                            case 'name':
                                                aValue = a.name?.toLowerCase() ?? '';
                                                bValue = b.name?.toLowerCase() ?? '';
                                                break;
                                            case 'podcast':
                                                aValue = a.podcast?.title?.toLowerCase() ?? '';
                                                bValue = b.podcast?.title?.toLowerCase() ?? '';
                                                break;
                                            case 'mentions':
                                                aValue = a.mentionCount ?? 0;
                                                bValue = b.mentionCount ?? 0;
                                                break;
                                            case 'viewsEnVivo':
                                            default:
                                                aValue = a.accumulatedLiveViewers ?? 0;
                                                bValue = b.accumulatedLiveViewers ?? 0;
                                                break;
                                        }
                                        if (typeof aValue === 'string' && typeof bValue === 'string') {
                                            if (aValue < bValue) return livestreamProgramsOrder === 'asc' ? -1 : 1;
                                            if (aValue > bValue) return livestreamProgramsOrder === 'asc' ? 1 : -1;
                                            return 0;
                                        }
                                        if (aValue < bValue) return livestreamProgramsOrder === 'asc' ? -1 : 1;
                                        if (aValue > bValue) return livestreamProgramsOrder === 'asc' ? 1 : -1;
                                        return 0;
                                    });
                                    if (platform === 'ig') return null;
                                    if (sortedLivestreamPrograms.length === 0) {
                                        return (
                                            <Box sx={{ mb: 4 }}>
                                                <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary' }}>
                                                    {isEnglish ? 'YouTube Livestream programs' : 'Programas Livestream YouTube'}
                                                </Typography>
                                                <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {isEnglish ? 'No YouTube livestreams with brand mentions in this period.' : 'No hay livestreams de YouTube con menciones de marca en este período.'}
                                                    </Typography>
                                                </Paper>
                                            </Box>
                                        );
                                    }
                                    return (
                                        <Box sx={{ mb: 5 }}>
                                            <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary' }}>
                                                {isEnglish ? 'YouTube Livestream programs' : 'Programas Livestream YouTube'}
                                            </Typography>
                                            <TableContainer component={Paper} variant="outlined">
                                                <Box
                                                    sx={{
                                                        display: { xs: 'flex', md: 'none' },
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        px: 2,
                                                        py: 1,
                                                        color: 'text.secondary',
                                                        borderBottom: '1px solid',
                                                        borderColor: 'divider',
                                                    }}
                                                >
                                                    <SwipeLeftIcon />
                                                    <Typography variant="caption">{isEnglish ? 'Swipe to see more' : 'Desliza para ver más'}</Typography>
                                                </Box>
                                                <Table>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={{ padding: { xs: '12px', sm: '16px' } }}>
                                                                <TableSortLabel
                                                                    active={livestreamProgramsOrderBy === 'name'}
                                                                    direction={livestreamProgramsOrderBy === 'name' ? livestreamProgramsOrder : 'asc'}
                                                                    onClick={() => handleLivestreamProgramsSort('name')}
                                                                >
                                                                    {isEnglish ? 'Program' : 'Programa'}
                                                                </TableSortLabel>
                                                            </TableCell>
                                                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                                                <TableSortLabel
                                                                    active={livestreamProgramsOrderBy === 'podcast'}
                                                                    direction={livestreamProgramsOrderBy === 'podcast' ? livestreamProgramsOrder : 'asc'}
                                                                    onClick={() => handleLivestreamProgramsSort('podcast')}
                                                                >
                                                                    {isEnglish ? 'Channel' : 'Canal'}
                                                                </TableSortLabel>
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ padding: { xs: '12px', sm: '16px' } }}>
                                                                <TableSortLabel
                                                                    active={livestreamProgramsOrderBy === 'mentions'}
                                                                    direction={livestreamProgramsOrderBy === 'mentions' ? livestreamProgramsOrder : 'desc'}
                                                                    onClick={() => handleLivestreamProgramsSort('mentions')}
                                                                >
                                                                    {isEnglish ? 'Mentions' : 'Menciones'}
                                                                </TableSortLabel>
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ padding: { xs: '12px', sm: '16px' } }}>
                                                                <TableSortLabel
                                                                    active={livestreamProgramsOrderBy === 'viewsEnVivo'}
                                                                    direction={livestreamProgramsOrderBy === 'viewsEnVivo' ? livestreamProgramsOrder : 'desc'}
                                                                    onClick={() => handleLivestreamProgramsSort('viewsEnVivo')}
                                                                >
                                                                    {isEnglish ? 'Views en vivo' : 'Esp. en vivo'}
                                                                </TableSortLabel>
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ width: { xs: '24px', sm: '40px' }, padding: { xs: '8px 4px', sm: '16px' } }} />
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {sortedLivestreamPrograms.map((program) => (
                                                            <TableRow
                                                                key={program._id}
                                                                hover
                                                                onClick={() => handleLivestreamProgramClick(program._id?.toString?.() ?? String(program._id))}
                                                                sx={{ cursor: 'pointer' }}
                                                            >
                                                                <TableCell sx={{ padding: { xs: '12px', sm: '16px' } }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        {program.imageS3Key ? (
                                                                            <Avatar
                                                                                src={ensureDirectS3Url(getS3Url(program.imageS3Key))}
                                                                                sx={{ width: 32, height: 32 }}
                                                                            />
                                                                        ) : (
                                                                            <Avatar sx={{ width: 32, height: 32 }}>
                                                                                <PlayArrowIcon />
                                                                            </Avatar>
                                                                        )}
                                                                        <Typography variant="body2" fontWeight="medium">
                                                                            {program.name}
                                                                        </Typography>
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                                                    {program.podcast ? (
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            {program.podcast.imageS3Key ? (
                                                                                <Avatar
                                                                                    src={ensureDirectS3Url(getS3Url(program.podcast.imageS3Key))}
                                                                                    sx={{ width: 24, height: 24 }}
                                                                                />
                                                                            ) : (
                                                                                <PodcastsIcon fontSize="small" />
                                                                            )}
                                                                            <Typography variant="body2">
                                                                                {program.podcast.title}
                                                                            </Typography>
                                                                        </Box>
                                                                    ) : (
                                                                        <Typography variant="body2" color="text.secondary">N/A</Typography>
                                                                    )}
                                                                </TableCell>
                                                                <TableCell align="right" sx={{ padding: { xs: '12px', sm: '16px' } }}>
                                                                    <Chip label={program.mentionCount} size="small" />
                                                                </TableCell>
                                                                <TableCell align="right" sx={{ padding: { xs: '12px', sm: '16px' } }}>
                                                                    <Typography variant="body2" fontWeight="medium">
                                                                        {formatNumber(program.accumulatedLiveViewers)}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell align="center" sx={{ width: { xs: '24px', sm: '40px' }, padding: { xs: '8px 4px', sm: '16px' } }}>
                                                                    <ChevronRightIcon sx={{ color: 'text.secondary', fontSize: { xs: '1rem', sm: '1.25rem' } }} />
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    );
                                })()}

                                {/* Formato corto: Instagram Reels, YouTube Shorts, TikTok (Tracker + social) or legacy Instagram & TikTok */}
                                {(creatorsData.length > 0 || instagramReelsMentions.length > 0 || youtubeAccountsData.length > 0 || tiktokVideosMentions.length > 0) && platform !== 'yt' && (
                                    <Box sx={{ mb: 5, mt: 5 }}>
                                        <Typography variant="h1" component="h2" sx={{ fontSize: '1.8rem', fontWeight: 600, mb: 3 }}>
                                            {isTracker ? (isEnglish ? 'Short form' : 'Contenido short-form') : 'Instagram & TikTok'}
                                        </Typography>

                                        {/* Resumen + Tendencias Semanales for short form: Contenido, Views, Interacciones, Creadores */}
                                        {shortFormSummary && (
                                            <Box sx={{ mb: 5 }}>
                                                <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                                                    {isEnglish ? 'Summary' : 'Resumen'}
                                                </Typography>
                                                <Grid container spacing={2}>
                                                    <Grid size={{ xs: 6, sm: 3 }}>
                                                        <Card variant="outlined">
                                                            <CardContent>
                                                                <Typography variant="h4" color="primary">
                                                                    {formatNumber(shortFormSummary.contenido)}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {isEnglish ? 'Content' : 'Contenido'}
                                                                </Typography>
                                                            </CardContent>
                                                        </Card>
                                                    </Grid>
                                                    <Grid size={{ xs: 6, sm: 3 }}>
                                                        <Card variant="outlined">
                                                            <CardContent>
                                                                <Typography variant="h4" color="primary">
                                                                    {formatNumber(shortFormSummary.views)}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Views
                                                                </Typography>
                                                            </CardContent>
                                                        </Card>
                                                    </Grid>
                                                    <Grid size={{ xs: 6, sm: 3 }}>
                                                        <Card variant="outlined">
                                                            <CardContent>
                                                                <Typography variant="h4" color="primary">
                                                                    {formatNumber(shortFormSummary.interactions)}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {isEnglish ? 'Interactions' : 'Interacciones'}
                                                                </Typography>
                                                            </CardContent>
                                                        </Card>
                                                    </Grid>
                                                    <Grid size={{ xs: 6, sm: 3 }}>
                                                        <Card variant="outlined">
                                                            <CardContent>
                                                                <Typography variant="h4" color="primary">
                                                                    {formatNumber(shortFormSummary.creators)}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {isEnglish ? 'Creators' : 'Creadores'}
                                                                </Typography>
                                                            </CardContent>
                                                        </Card>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        )}
                                        {socialWeeklyTrends.length > 0 && (
                                            <Box sx={{ mb: 5 }}>
                                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                                                    {isEnglish ? 'Weekly Trends' : 'Tendencias Semanales'}
                                                </Typography>
                                                <Grid container spacing={2}>
                                                    <Grid size={{ xs: 6, lg: 3 }}>
                                                        <TrendChartCard
                                                            title={isEnglish ? 'Content' : 'Contenido'}
                                                            dataKey="reels"
                                                            color="#2e7d32"
                                                            tooltipLabel={isEnglish ? 'Reels / videos' : 'Reels / videos'}
                                                            granularityTooltip={isEnglish ? 'Week' : 'Semana'}
                                                            data={socialWeeklyTrends}
                                                            median={calculateMedian(socialWeeklyTrends.map(t => t.reels))}
                                                            allowDecimals={false}
                                                        />
                                                    </Grid>
                                                    <Grid size={{ xs: 6, lg: 3 }}>
                                                        <TrendChartCard
                                                            title="Views"
                                                            dataKey="views"
                                                            color="#1976d2"
                                                            tooltipLabel="Views"
                                                            granularityTooltip={isEnglish ? 'Week' : 'Semana'}
                                                            data={socialWeeklyTrends}
                                                            median={calculateMedian(socialWeeklyTrends.map(t => t.views))}
                                                            formatValue={formatCompact}
                                                            yAxisWidth={45}
                                                        />
                                                    </Grid>
                                                    <Grid size={{ xs: 6, lg: 3 }}>
                                                        <TrendChartCard
                                                            title={isEnglish ? 'Interactions' : 'Interacciones'}
                                                            dataKey="interactions"
                                                            color="#ed6c02"
                                                            tooltipLabel={isEnglish ? 'Interactions' : 'Interacciones'}
                                                            granularityTooltip={isEnglish ? 'Week' : 'Semana'}
                                                            data={socialWeeklyTrends}
                                                            median={calculateMedian(socialWeeklyTrends.map(t => t.interactions))}
                                                            formatValue={formatCompact}
                                                            yAxisWidth={45}
                                                        />
                                                    </Grid>
                                                    <Grid size={{ xs: 6, lg: 3 }}>
                                                        <TrendChartCard
                                                            title={isEnglish ? 'Creators' : 'Creadores'}
                                                            dataKey="creators"
                                                            color="#9c27b0"
                                                            tooltipLabel={isEnglish ? 'Creators' : 'Creadores'}
                                                            granularityTooltip={isEnglish ? 'Week' : 'Semana'}
                                                            data={socialWeeklyTrends}
                                                            median={calculateMedian(socialWeeklyTrends.map(t => t.creators))}
                                                            allowDecimals={false}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        )}

                                        {/* Sectioned view: 3 platform tables (same format as VOD programs) */}
                                        {isTracker ? (
                                            <>
                                                {/* Instagram Reels — heading + table */}
                                                <Box sx={{ mb: 5 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                                        <Typography variant="h6" component="h3" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                                            Instagram Reels
                                                        </Typography>
                                                    </Box>
                                                    {creatorsDataInstagram.length === 0 ? (
                                                        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                                            <Typography variant="body2" color="text.secondary">{isEnglish ? 'No Instagram Reels.' : 'No hay reels de Instagram.'}</Typography>
                                                        </Paper>
                                                    ) : (
                                                        <ShortFormCreatorsTable
                                                            firstColumnLabel={isEnglish ? 'Creator' : 'Creador'}
                                                            countColumnLabel="Reels"
                                                            nameSortKey="username"
                                                            countSortKey="totalReels"
                                                            orderBy={creatorsOrderBy as ShortFormTableSortKey}
                                                            order={creatorsOrder}
                                                            onSort={(p) => handleCreatorsSort(p as typeof creatorsOrderBy)}
                                                            rows={sortedCreatorsDataInstagram.map((creator): ShortFormCreatorsTableRow => ({
                                                                id: creator.creatorId,
                                                                avatarUrl: creator.profilePicS3Key ? ensureDirectS3Url(getS3Url(creator.profilePicS3Key)) : creator.profilePicUrl,
                                                                label: `@${creator.username}`,
                                                                avatarFallback: creator.username.slice(0, 1),
                                                                count: creator.totalReels,
                                                                views: creator.totalViews,
                                                                likes: creator.totalLikes,
                                                                comments: creator.totalComments,
                                                            }))}
                                                            onRowClick={(id) => handleCreatorClick(id, 0, 'instagram')}
                                                            onRowMouseEnter={(id) => prefetchCreatorDetail(id, 0, 'instagram')}
                                                            isEnglish={isEnglish}
                                                            formatNumber={formatNumber}
                                                        />
                                                    )}
                                                </Box>

                                                {/* TikTok Videos — heading + table */}
                                                <Box sx={{ mb: 5 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                                        <Typography variant="h6" component="h3" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                                            TikTok Videos
                                                        </Typography>
                                                    </Box>
                                                    {creatorsDataTiktok.length === 0 ? (
                                                        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                                            <Typography variant="body2" color="text.secondary">{isEnglish ? 'No TikTok videos.' : 'No hay videos de TikTok.'}</Typography>
                                                        </Paper>
                                                    ) : (
                                                        <ShortFormCreatorsTable
                                                            firstColumnLabel={isEnglish ? 'Creator' : 'Creador'}
                                                            countColumnLabel={isEnglish ? 'Videos' : 'Videos'}
                                                            nameSortKey="username"
                                                            countSortKey="totalReels"
                                                            orderBy={creatorsOrderBy as ShortFormTableSortKey}
                                                            order={creatorsOrder}
                                                            onSort={(p) => handleCreatorsSort(p as typeof creatorsOrderBy)}
                                                            rows={sortedCreatorsDataTiktok.map((creator): ShortFormCreatorsTableRow => ({
                                                                id: creator.creatorId,
                                                                avatarUrl: creator.profilePicS3Key ? ensureDirectS3Url(getS3Url(creator.profilePicS3Key)) : creator.profilePicUrl,
                                                                label: `@${creator.username}`,
                                                                avatarFallback: creator.username.slice(0, 1),
                                                                count: creator.totalReels,
                                                                views: creator.totalViews,
                                                                likes: creator.totalLikes,
                                                                comments: creator.totalComments,
                                                            }))}
                                                            onRowClick={(id) => handleCreatorClick(id, 0, 'tiktok')}
                                                            onRowMouseEnter={(id) => prefetchCreatorDetail(id, 0, 'tiktok')}
                                                            isEnglish={isEnglish}
                                                            formatNumber={formatNumber}
                                                        />
                                                    )}
                                                </Box>

                                                {/* YouTube Shorts — heading + table by account (same flow as IG/TikTok) */}
                                                <Box sx={{ mb: 5 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                                        <Typography variant="h6" component="h3" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                                            YouTube Shorts
                                                        </Typography>
                                                    </Box>
                                                    {youtubeAccountsData.length === 0 ? (
                                                        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                                            <Typography variant="body2" color="text.secondary">{isEnglish ? 'No YouTube Shorts.' : 'No hay YouTube Shorts.'}</Typography>
                                                        </Paper>
                                                    ) : (
                                                        <ShortFormCreatorsTable
                                                            firstColumnLabel={isEnglish ? 'Channel' : 'Canal'}
                                                            countColumnLabel={isEnglish ? 'Shorts' : 'Shorts'}
                                                            nameSortKey="channelName"
                                                            countSortKey="totalShorts"
                                                            orderBy={shortFormYoutubeOrderBy}
                                                            order={shortFormYoutubeOrder}
                                                            onSort={(p) => handleShortFormYoutubeSort(p as typeof shortFormYoutubeOrderBy)}
                                                            rows={sortedYoutubeAccountsData.map((account): ShortFormCreatorsTableRow => ({
                                                                id: account.channelId,
                                                                avatarUrl: account.imageS3Key ? ensureDirectS3Url(getS3Url(account.imageS3Key)) : account.imageUrl,
                                                                label: account.channelName,
                                                                avatarFallback: account.channelName.slice(0, 1),
                                                                count: account.totalShorts,
                                                                views: account.totalViews,
                                                                likes: account.totalLikes,
                                                                comments: account.totalComments,
                                                            }))}
                                                            onRowClick={(id) => handleYoutubeShortsCreatorClick(id)}
                                                            isEnglish={isEnglish}
                                                            formatNumber={formatNumber}
                                                        />
                                                    )}
                                                </Box>
                                            </>
                                        ) : (
                                            <>
                                                <Box sx={{ mb: 4 }}>
                                                    <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                                                        {isEnglish ? 'Creators' : 'Creadores'}
                                                    </Typography>
                                                    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                                                        <Box
                                                            sx={{
                                                                display: { xs: 'flex', md: 'none' },
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                px: 2,
                                                                py: 1,
                                                                color: 'text.secondary',
                                                                borderBottom: '1px solid',
                                                                borderColor: 'divider',
                                                            }}
                                                        >
                                                            <SwipeLeftIcon />
                                                            <Typography variant="caption">Desliza para ver más</Typography>
                                                        </Box>
                                                        <Table sx={{ tableLayout: 'auto', width: '100%' }}>
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell sx={{ minWidth: { xs: '100px', sm: '150px' }, padding: { xs: '8px', sm: '16px' } }}>
                                                                        <TableSortLabel
                                                                            active={creatorsOrderBy === 'username'}
                                                                            direction={creatorsOrderBy === 'username' ? creatorsOrder : 'asc'}
                                                                            onClick={() => handleCreatorsSort('username')}
                                                                        >
                                                                            {isEnglish ? 'Creator' : 'Creador'}
                                                                        </TableSortLabel>
                                                                    </TableCell>
                                                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', padding: { xs: '8px', sm: '16px' } }}>
                                                                        <TableSortLabel
                                                                            active={creatorsOrderBy === 'totalReels'}
                                                                            direction={creatorsOrderBy === 'totalReels' ? creatorsOrder : 'desc'}
                                                                            onClick={() => handleCreatorsSort('totalReels')}
                                                                        >
                                                                            Reels
                                                                        </TableSortLabel>
                                                                    </TableCell>
                                                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', padding: { xs: '8px', sm: '16px' } }}>
                                                                        <TableSortLabel
                                                                            active={creatorsOrderBy === 'totalViews'}
                                                                            direction={creatorsOrderBy === 'totalViews' ? creatorsOrder : 'desc'}
                                                                            onClick={() => handleCreatorsSort('totalViews')}
                                                                        >
                                                                            Views
                                                                        </TableSortLabel>
                                                                    </TableCell>
                                                                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, whiteSpace: 'nowrap' }}>
                                                                        <TableSortLabel
                                                                            active={creatorsOrderBy === 'totalLikes'}
                                                                            direction={creatorsOrderBy === 'totalLikes' ? creatorsOrder : 'desc'}
                                                                            onClick={() => handleCreatorsSort('totalLikes')}
                                                                        >
                                                                            Likes
                                                                        </TableSortLabel>
                                                                    </TableCell>
                                                                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, whiteSpace: 'nowrap' }}>
                                                                        <TableSortLabel
                                                                            active={creatorsOrderBy === 'totalComments'}
                                                                            direction={creatorsOrderBy === 'totalComments' ? creatorsOrder : 'desc'}
                                                                            onClick={() => handleCreatorsSort('totalComments')}
                                                                        >
                                                                            {isEnglish ? 'Comments' : 'Comentarios'}
                                                                        </TableSortLabel>
                                                                    </TableCell>
                                                                    <TableCell align="center" sx={{ width: { xs: '24px', sm: '40px' }, padding: { xs: '8px 4px', sm: '16px' } }}>
                                                                    </TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {sortedCreatorsData.map((creator) => (
                                                                    <TableRow
                                                                        key={creator.creatorId}
                                                                        hover
                                                                        onClick={() => handleCreatorClick(creator.creatorId, 0, creator.platform)}
                                                                        onMouseEnter={() => prefetchCreatorDetail(creator.creatorId, 0, creator.platform)}
                                                                        sx={{ cursor: 'pointer' }}
                                                                    >
                                                                        <TableCell sx={{ overflow: 'hidden', padding: { xs: '12px', sm: '16px' } }}>
                                                                            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5 }}>
                                                                                {creator.profilePicS3Key ? (
                                                                                    <Avatar
                                                                                        src={ensureDirectS3Url(getS3Url(creator.profilePicS3Key))}
                                                                                        sx={{ width: 24, height: 24 }}
                                                                                    />
                                                                                ) : creator.profilePicUrl ? (
                                                                                    <Avatar
                                                                                        src={creator.profilePicUrl}
                                                                                        sx={{ width: 24, height: 24 }}
                                                                                    />
                                                                                ) : (
                                                                                    <Avatar sx={{ width: 24, height: 24 }}>
                                                                                        {creator.platform === 'tiktok' ? <TikTokIcon fontSize="small" /> : <InstagramIcon fontSize="small" />}
                                                                                    </Avatar>
                                                                                )}
                                                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25, width: '100%', overflow: 'hidden' }}>
                                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', overflow: 'hidden' }}>
                                                                                        {creator.platform === 'tiktok' ? (
                                                                                            <TikTokIcon sx={{ fontSize: '0.875rem', flexShrink: 0 }} />
                                                                                        ) : (
                                                                                            <InstagramIcon sx={{ fontSize: '0.875rem', flexShrink: 0 }} />
                                                                                        )}
                                                                                        <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                                            @{creator.username}
                                                                                        </Typography>
                                                                                        {creator.isVerified && (
                                                                                            <InstagramIcon color="primary" sx={{ fontSize: '0.875rem', flexShrink: 0 }} />
                                                                                        )}
                                                                                    </Box>
                                                                                    {creator.followersCount && (
                                                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                                                                                            {formatNumber(creator.followersCount)} {isEnglish ? 'followers' : 'seguidores'}
                                                                                        </Typography>
                                                                                    )}
                                                                                </Box>
                                                                            </Box>
                                                                        </TableCell>
                                                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', padding: { xs: '12px', sm: '16px' } }}>
                                                                            <Chip label={creator.totalReels} size="small" />
                                                                        </TableCell>
                                                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', padding: { xs: '12px', sm: '16px' } }}>
                                                                            <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.875rem' }}>
                                                                                {formatNumber(creator.totalViews)}
                                                                            </Typography>
                                                                        </TableCell>
                                                                        <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, whiteSpace: 'nowrap' }}>
                                                                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                                                                {formatNumber(creator.totalLikes)}
                                                                            </Typography>
                                                                        </TableCell>
                                                                        <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, whiteSpace: 'nowrap' }}>
                                                                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                                                                {formatNumber(creator.totalComments)}
                                                                            </Typography>
                                                                        </TableCell>
                                                                        <TableCell align="center" sx={{ width: { xs: '24px', sm: '40px' }, padding: { xs: '8px 4px', sm: '16px' } }}>
                                                                            <ChevronRightIcon sx={{ color: 'text.secondary', fontSize: { xs: '1rem', sm: '1.25rem' } }} />
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </Box>
                                            </>
                                        )}
                                    </Box>
                                )}

                                {/* Empty State */}
                                {!brandDataLoading && !brandDataError && (!currentPeriodData || currentPeriodData.length === 0) && (
                                    <Alert severity="info">
                                        {isEnglish ? 'No data available for the selected brand and year.' : 'No hay datos disponibles para la marca y año seleccionados.'}
                                    </Alert>
                                )}
                            </>
                        )}
                    </>
                )}
            </Container>
        </>
    );
};

export default BrandExplorerYear;

